import Divider from 'LuxKit/Divider'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalImageThumbnail from 'LuxKit/Modal/ModalImageThumbnail'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Image from 'components/Common/Image'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import { ImageParams } from 'components/Common/ResponsiveImage'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import { EXP_CATEGORY_AIRPORT_LOUNGE_ID } from 'constants/experience'
import useExperienceList from 'hooks/Experiences/useExperienceList'
import LoyaltyTierBenefitAvailabilityMessageBanner from 'loyalty/components/LoyaltyTierBenefitAvailabilityMessageBanner'
import React from 'react'

const heroParams: ImageParams = {
  aspectRatio: '16:8',
  mobileWidth: '720px',
  fit: 'center',
}

interface Props {
  image: App.Image
}

function LandingPageLoyaltyLoungePassBenefitsModal(props: Props) {
  const { image } = props
  const [{ fetching: fetchingExperiences }, experiences] = useExperienceList({ categoryCodes: [EXP_CATEGORY_AIRPORT_LOUNGE_ID], isLoyaltyEligible: true, limit: 100 }, false)

  return (
    <ModalBase size="L">
      <ModalHeader title="Free airport lounge passes" />
      <ModalBody>
        <ModalImageThumbnail image={image} imageParams={heroParams} />
        <ModalContent>
          <Group direction="vertical" gap={32}>
            <LoyaltyTierBenefitAvailabilityMessageBanner benefitType="lounge_pass" />
            <BodyText variant="large">
              Kick off your trip in comfort with complimentary access to select airport lounges. Recharge with a drink, a bite to eat, and space to relax before your international flight. Simply add to your booking when checking out for your overseas escapes.
            </BodyText>
            <VerticalSpacer gap={8}>
              <Heading variant="heading4">How it works</Heading>
              <BodyText variant="large">
                When booking an overseas hotel, you can add complimentary lounge passes at checkout. You will be able to redeem all your available passes, and buy extras if you are travelling with others.
              </BodyText>
            </VerticalSpacer>
            <Heading variant="heading4">Eligible airport lounges</Heading>
            <VerticalSpacer gap={12}>
              {(fetchingExperiences) && <VerticalSpacer gap={12}>
                <LoadingBox height={84} />
                <Divider kind="primary" />
                <LoadingBox height={84} />
                <Divider kind="primary" />
                <LoadingBox height={84} />
                <Divider kind="primary" />
                <LoadingBox height={84} />
                <Divider kind="primary" />
              </VerticalSpacer>}
              {!fetchingExperiences && experiences.map((experience) => <React.Fragment key={experience.id}>
                <Group direction="horizontal" gap={20} verticalAlign="center">
                  <Image
                    image={experience.images[0]}
                    aspectRatio="5:3"
                    width="140px"
                  />
                  <Group direction="vertical" gap={4}>
                    <BodyTextBlock variant="small" weight="bold" startIcon={<LineMapMarkerIcon aria-hidden="true" />}>
                      {experience.redemptionLocations?.length > 1 ? 'Multiple Locations' : experience.location.name}
                    </BodyTextBlock>
                    <Heading variant="heading6">{experience.name}</Heading>
                  </Group>
                </Group>
                <Divider kind="primary" />
              </React.Fragment>)}
              <BodyText variant="small">
                Lounge access is available exclusively at the above airports.
              </BodyText>
            </VerticalSpacer>
          </Group>
        </ModalContent>
      </ModalBody>
    </ModalBase>
  )
}

export default LandingPageLoyaltyLoungePassBenefitsModal
